import React, { useEffect, useMemo, useState } from 'react';
import { getTranslation } from '../../../../../utils/translation';
import RangePicker from '../../../../shared/Point/Filters/RangePicker/RangePicker';
import {
    getTerminalsBalance,
    printBalanceReportTerminals,
} from '../../../../../store/actions/reports';
import { useDispatch, useSelector } from 'react-redux';
import * as types from '../../../../../store/actions/types';
import { BALANCE_REPORTS_FILTER_PERIODS } from '../../../../../constants/defaultValues';
import Select from 'react-select';
import { getTerminals } from '../../../../../store/actions/terminals';
import moment from 'moment';

const rangePeriod = BALANCE_REPORTS_FILTER_PERIODS.find(period => period.key === 'range');
const initialPeriod = BALANCE_REPORTS_FILTER_PERIODS[0];

const Filters = () => {
    const dispatch = useDispatch();

    const accountNumber = useSelector(state => state.settings.account?.data?.accountNumber ?? '');
    const { terminals } = useSelector(state => state.terminals);
    const { terminalsBalance } = useSelector(state => state.reports);

    const [selectedPeriod, setSelectedPeriod] = useState(initialPeriod);
    const [dates, setDates] = useState(Object.values(initialPeriod.getRange()));
    const [startDate, endDate] = dates;

    const terminalsSelectData = useMemo(() => {
        return [
            {
                value: 'all',
                label: getTranslation('balanceReportsAllTerminals'),
            },
            ...terminals.map(terminal => ({
                value: terminal.terminalCode,
                label: terminal.terminalCode,
            })),
        ];
    }, [terminals]);

    const [terminal, setTerminal] = useState(terminalsSelectData[0]);

    const clearDates = () => {
        setDates([null, null]);
    };

    const clearReportData = () => {
        dispatch({
            type: types.GET_REPORT_TERMINALS_BALANCE,
            payload: null,
        });
    };

    const clearAll = () => {
        clearDates();
        clearReportData();
        setTerminal(terminalsSelectData[0]);
    };

    const getReportData = (startDate, endDate) => {
        if (startDate && endDate) {
            dispatch(getTerminalsBalance({ startDate, endDate, terminals: terminal.value }));
        }
    };

    const printReportData = () => {
        printBalanceReportTerminals({
            issueDate: moment().format('DD/MM/YY HH:mm:ss'),
            periodStartDate: moment(startDate).format('DD/MM/YY'),
            periodEndDate: moment(endDate).format('DD/MM/YY'),
            periodType: selectedPeriod.periodType,
            terminalCode: terminal.label,
            cashierCode: accountNumber,
            ...terminalsBalance,
        });
    };

    const onReset = () => {
        clearAll();
        setSelectedPeriod(rangePeriod);
    };

    const onPeriodChange = async period => {
        clearAll();
        setSelectedPeriod(period);

        if (period.key !== rangePeriod.key) {
            const { startTime, endTime } = await period.getRange();
            setDates([startTime, endTime]);
            getReportData({ startTime, endTime, terminals: terminal.value });
        }
    };

    const handleDateChange = (date, callback) => {
        if (date) {
            clearReportData();
            setSelectedPeriod(rangePeriod);
            callback();
        }
    };

    const onStartDateChange = date => {
        handleDateChange(date, () => {
            setDates(() => [date.getTime(), endDate]);
        });
    };

    const onEndDateChange = date => {
        handleDateChange(date, () => {
            setDates(() => [startDate, date.getTime()]);
        });
    };

    const onTerminalChange = value => {
        setTerminal(value);
    };

    useEffect(() => {
        getReportData(startDate, endDate);
        dispatch(
            getTerminals({ startTime: startDate, endTime: endDate, terminals: terminal.value }),
        );
    }, []);

    return (
        <div className={'filters'}>
            <Select
                className={`common-select common-select--filters`}
                classNamePrefix='common'
                menuPlacement={'auto'}
                isSearchable={false}
                placeholder={getTranslation('balanceReportTerminalsSelectPlaceholder')}
                options={terminalsSelectData}
                defaultValue={terminalsSelectData[0]}
                onChange={onTerminalChange}
                value={terminal}
            />
            <RangePicker
                periods={BALANCE_REPORTS_FILTER_PERIODS}
                selectedPeriod={selectedPeriod}
                onPeriodChange={onPeriodChange}
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={onStartDateChange}
                onEndDateChange={onEndDateChange}
                onSearch={getReportData}
                onReset={onReset}
                showTime
            />
            <div className='m-l-auto' onClick={printReportData}>
                <button className='filter-button'>{getTranslation('print')}</button>
            </div>
        </div>
    );
};

export default Filters;
